"use client";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import { fetchCompanyProfileAction } from "../../slices/companyProfileSlice";
import { useNotification } from "@/context/NotificationContext";
import { tradeRevenueSchema, TradeRevenueFormData } from "../../schemas/settingsSchemas";
import { updateTradeRevenue } from "@/api/updateTradeRevenue/updateTradeRevenueApi";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface TradeConfig {
  key: keyof TradeRevenueFormData;
  statusKey: "asiStatus" | "esiStatus" | "psiStatus" | "rsiStatus";
  label: string;
  description: string;
}

const TRADE_CONFIGS: TradeConfig[] = [
  {
    key: "asiRevenue",
    statusKey: "asiStatus",
    label: "ASI (HVAC)",
    description: "Air System Industry - Heating, Ventilation, and Air Conditioning",
  },
  {
    key: "esiRevenue",
    statusKey: "esiStatus",
    label: "ESI (Electrical)",
    description: "Electrical Service Industry",
  },
  {
    key: "psiRevenue",
    statusKey: "psiStatus",
    label: "PSI (Plumbing)",
    description: "Plumbing Service Industry",
  },
  {
    key: "rsiRevenue",
    statusKey: "rsiStatus",
    label: "RSI (Roofing)",
    description: "Roofing Service Industry",
  },
];

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const parseCurrency = (value: string): number | null => {
  if (!value) return null;
  const cleaned = value.replace(/[^0-9]/g, "");
  const parsed = parseInt(cleaned, 10);
  return isNaN(parsed) ? null : parsed;
};

const AnnualRevenue: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showNotification } = useNotification();
  const [saving, setSaving] = useState(false);

  const { companyProfile, loading } = useSelector(
    (state: RootState) => state.companyProfile
  );

  const form = useForm<TradeRevenueFormData>({
    resolver: zodResolver(tradeRevenueSchema),
    defaultValues: {
      asiRevenue: null,
      esiRevenue: null,
      psiRevenue: null,
      rsiRevenue: null,
    },
    mode: "onChange",
  });

  useEffect(() => {
    dispatch(fetchCompanyProfileAction());
  }, [dispatch]);

  useEffect(() => {
    if (companyProfile) {
      form.reset({
        asiRevenue: companyProfile.asiRevenue ?? null,
        esiRevenue: companyProfile.esiRevenue ?? null,
        psiRevenue: companyProfile.psiRevenue ?? null,
        rsiRevenue: companyProfile.rsiRevenue ?? null,
      });
    }
  }, [companyProfile, form]);

  const onSubmit = async (data: TradeRevenueFormData) => {
    try {
      setSaving(true);
      await updateTradeRevenue({
        asiRevenue: data.asiRevenue,
        esiRevenue: data.esiRevenue,
        psiRevenue: data.psiRevenue,
        rsiRevenue: data.rsiRevenue,
      });

      // Refresh company profile to get updated values
      dispatch(fetchCompanyProfileAction());

      showNotification(
        "Successfully updated annual revenue!",
        "Your trade revenue information has been saved.",
        "success"
      );
    } catch (error) {
      showNotification(
        "Failed to update annual revenue",
        "Please try again later.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (companyProfile) {
      form.reset({
        asiRevenue: companyProfile.asiRevenue ?? null,
        esiRevenue: companyProfile.esiRevenue ?? null,
        psiRevenue: companyProfile.psiRevenue ?? null,
        rsiRevenue: companyProfile.rsiRevenue ?? null,
      });
    }
  };

  // Filter to only show active trades
  const activeTrades = TRADE_CONFIGS.filter((trade) => {
    if (!companyProfile) return false;
    return companyProfile[trade.statusKey] === "Active";
  });

  const hasNoActiveTrades = activeTrades.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Annual Revenue</CardTitle>
        <CardDescription>
          Enter your company&apos;s annual revenue for each active trade. This information helps us provide better benchmarking and insights.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500">
            Loading trade information...
          </div>
        ) : hasNoActiveTrades ? (
          <div className="text-center py-8 text-gray-500">
            <p className="mb-2">No active trades found for your company.</p>
            <p className="text-sm">
              Contact support if you believe this is an error.
            </p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {activeTrades.map((trade) => (
                <Controller
                  key={trade.key}
                  control={form.control}
                  name={trade.key}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{trade.label}</FormLabel>
                      <p className="text-sm text-muted-foreground mb-2">
                        {trade.description}
                      </p>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                            $
                          </span>
                          <Input
                            className="pl-7"
                            placeholder="0"
                            disabled={loading || saving}
                            value={formatCurrency(field.value)}
                            onChange={(e) => {
                              const parsed = parseCurrency(e.target.value);
                              field.onChange(parsed);
                            }}
                            onBlur={field.onBlur}
                          />
                        </div>
                      </FormControl>
                      {fieldState.error && (
                        <FormMessage>{fieldState.error.message}</FormMessage>
                      )}
                    </FormItem>
                  )}
                />
              ))}

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading || saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
};

export default AnnualRevenue;
